import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Loader2 } from 'lucide-react';
import { apiClient } from '../../api/client';

export default function OAuthCallback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(true);
  
  useEffect(() => {
    
    const handleCallback = async () => {
      try {
        
        // Extract authorization code from URL params
        const code = searchParams.get('code');
        const state = searchParams.get('state');
        
        
        if (!code) {
          throw new Error('No authorization code received from OAuth provider');
        }

        
        // Call backend to exchange code for tokens
        const response = await fetch('/api/auth/callback', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ code, state }),
        });
        

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`OAuth callback failed: ${errorText}`);
        }

        const result = await response.json();
        
        // The backend should return user info and session token
        if (result.session_token && result.user) {
          
          // Update API client with session token (this also stores in localStorage with correct key)
          apiClient.setSessionToken(result.session_token);
          
          // Store user info
          localStorage.setItem('user', JSON.stringify(result.user));
          
          
          // Redirect to home - the useAuth hook will detect the stored session
          navigate('/home', { replace: true });
        } else {
          throw new Error('Invalid response from OAuth callback');
        }

      } catch (err: any) {
        setError(err.message || 'Authentication failed. Please try again.');
        setIsProcessing(false);
        
        
        // Redirect to login after a delay
        setTimeout(() => {
          navigate('/login', { replace: true });
        }, 3000);
      }
    };

    handleCallback();
  }, [searchParams, navigate]);

  if (isProcessing) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle className="flex items-center justify-center gap-2">
              <Loader2 className="h-5 w-5 animate-spin" />
              Completing Sign In
            </CardTitle>
            <CardDescription>
              Please wait while we complete your authentication...
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-center text-sm text-muted-foreground">
              This may take a few seconds
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle className="text-destructive">Authentication Failed</CardTitle>
            <CardDescription>
              There was an error completing your sign in
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-center space-y-4">
              <p className="text-sm text-muted-foreground">
                {error}
              </p>
              <p className="text-xs text-muted-foreground">
                Redirecting to login page...
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return null;
}